/**
 * IP addresses typed into the UI. The ad server targets IPv4 only, either a
 * single address or a CIDR range whose prefix is between /8 and /32 — a range
 * wider than a /8 is more addresses than targeting is meant to cover.
 */

/** The narrowest prefix the ad server packs a subnet with, from `IPUtil`. */
const SMALLEST_PREFIX = 8

const IPV4 = /^(25[0-5]|2[0-4]\d|[01]?\d\d?)(\.(25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/

/**
 * The ranges no ad request can legitimately come from, as `IPUtil` lists them:
 * the unspecified and loopback addresses, link-local, multicast, and the three
 * private networks. Targeting one of these would never deliver.
 */
const PRIVATE_AND_LOCAL = [
  '0.0.0.0/32',
  '127.0.0.1/32',
  '169.254.0.0/16',
  '224.0.0.0/4',
  '10.0.0.0/8',
  '172.16.0.0/12',
  '192.168.0.0/16',
]

/** An inclusive range of addresses, each as an unsigned 32-bit number. */
interface AddressRange {
  start: number
  end: number
}

/** True for a plain dotted-quad IPv4 address, no subnet. */
export function isIpv4Address(value: string): boolean {
  return IPV4.test(value.trim())
}

function toNumber(address: string): number {
  return address.split('.').reduce((n, octet) => n * 256 + Number(octet), 0)
}

/**
 * The addresses an entry covers, for any prefix from /0 to /32, or undefined
 * when it is not an IPv4 address at all. The prefix the ad server will accept
 * is narrower than this — {@link isIpv4AddressOrSubnet} is what applies that.
 */
function rangeOf(value: string): (AddressRange & { prefix: number }) | undefined {
  const [address, prefix, ...rest] = value.trim().split('/')
  if (rest.length > 0 || !isIpv4Address(address)) return undefined
  const start = toNumber(address)
  if (prefix === undefined) return { start, end: start, prefix: 32 }
  if (!/^\d{1,2}$/.test(prefix)) return undefined
  const size = Number(prefix)
  if (size > 32) return undefined
  // The range runs from the address with its host bits cleared, the way the
  // server reads a subnet, to that address plus every host in it.
  const hosts = 2 ** (32 - size)
  const network = start - (start % hosts)
  return { start: network, end: network + hosts - 1, prefix: size }
}

const BLOCKED: AddressRange[] = PRIVATE_AND_LOCAL.map((cidr) => rangeOf(cidr)!)

const isBlocked = (range: AddressRange) =>
  BLOCKED.some((blocked) => range.start <= blocked.end && blocked.start <= range.end)

/**
 * True for an address the IP targeting accepts: an IPv4 address on its own, or
 * one with a `/8`–`/32` subnet, e.g. `205.112.45.0/24`, that does not reach
 * into a private or local range.
 */
export function isIpv4AddressOrSubnet(value: string): boolean {
  const range = rangeOf(value)
  return !!range && range.prefix >= SMALLEST_PREFIX && !isBlocked(range)
}

/**
 * True when the value is an address the targeting would otherwise take, but one
 * that could never deliver. A subnet too wide to target at all is not reported
 * here, so that the wider problem is the one the user is told about.
 */
export function isPrivateOrLocalIpv4(value: string): boolean {
  const range = rangeOf(value)
  return !!range && range.prefix >= SMALLEST_PREFIX && isBlocked(range)
}
